// Taxes API export - 2024 - Notes API added
import axios from 'axios';
import { Product, Order, User, Category, LoginRequest, LoginResponse, CreateOrderRequest } from '../types';
import { initializeCSRF } from '../App';
import { secureSetItem, secureGetItem, secureRemoveItem } from '../utils/secureStorage';

// API base URL
const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

// Axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 60000, // 60 saniye timeout
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // CSRF token için cookie'leri gönder
});

// CSRF token'ı al ve sakla
let csrfToken: string | null = null;

// CSRF token'ı al
const getCSRFToken = async (force: boolean = false): Promise<string> => {
  if (!csrfToken || force) {
    try {
      const response = await axios.get(`${API_URL}/api/csrf-token`, {
        withCredentials: true
      });
      csrfToken = response.data.csrfToken;
    } catch (error) {
      // CSRF token alınamadı
    }
  }
  return csrfToken || '';
};

// Request deduplication - timestamp bazlı (sadece çok kısa sürede gelen aynı istekleri engelle)
const pendingRequests = new Map<string, number>();
const DEDUP_TIMEOUT_MS = 1000; // 1 saniye içinde gelen aynı istekleri engelle

// Request interceptor to add auth token and CSRF token
api.interceptors.request.use(async (config) => {
  const token = secureGetItem('token');

  // Request deduplication - sadece orders endpoint'i için, sadece çok kısa süre içinde gelen istekleri engelle
  if (config.url === '/orders' && config.method === 'post') {
    const now = Date.now();
    const requestKey = `${config.method}:${config.url}:${JSON.stringify(config.data || {})}`;
    const lastRequestTime = pendingRequests.get(requestKey);

    // Eğer aynı istek 1 saniye içinde tekrar geliyorsa (double-click), engelle
    if (lastRequestTime && (now - lastRequestTime) < DEDUP_TIMEOUT_MS) {
      throw new Error('Lütfen bekleyiniz, işlem devam ediyor...');
    }

    // İsteği kaydet
    pendingRequests.set(requestKey, now);

    // Timeout sonrası temizle
    setTimeout(() => {
      const savedTime = pendingRequests.get(requestKey);
      if (savedTime === now) {
        pendingRequests.delete(requestKey);
      }
    }, DEDUP_TIMEOUT_MS);
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // CSRF token'ı ekle (GET, HEAD, OPTIONS hariç)
  if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
    const csrf = await getCSRFToken();
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }

  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // CSRF token hatası durumunda token'ı yenile
    if (error.response?.status === 403 && (error.response?.data?.code === 'CSRF_TOKEN_MISSING' || error.response?.data?.code === 'CSRF_TOKEN_INVALID')) {
      // Döngüyü önle - eğer bu zaten retry edilmiş bir istek ise tekrar deneme
      if (error.config._retry) {
        return Promise.reject(error);
      }

      // İsteği tekrar dene
      const originalRequest = error.config;
      originalRequest._retry = true; // Retry flag'i ekle

      // CSRF token'ı force yenile
      const csrf = await getCSRFToken(true);
      if (csrf) {
        originalRequest.headers['X-CSRF-Token'] = csrf;
        return api(originalRequest);
      }
    }

    if (error.response?.status === 401) {
      const originalRequest = error.config;

      // Infinite loop'u önle - eğer bu zaten retry edilmiş bir istek ise logout yap
      if (originalRequest._retry) {
        secureRemoveItem('token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Token refresh endpoint'ine gidiyorsak direkt logout yap
      if (originalRequest.url?.includes('/users/refresh')) {
        secureRemoveItem('token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Token süresi dolmuş olabilir, tokenManager ile yenilemeyi dene
      originalRequest._retry = true; // Retry flag'i ekle

      try {
        const { tokenManager } = await import('../utils/tokenManager');
        const refreshed = await tokenManager.checkAndRefreshToken();

        if (refreshed) {
          // Token yenilenirse, orijinal isteği tekrar dene
          const newToken = secureGetItem('token');
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        }
      } catch (tokenError) {
        // Token yenileme başarısız
      }

      // Token yenilenemezse logout yap
      secureRemoveItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  logout: async (): Promise<void> => {
    secureRemoveItem('token');
  },

  refreshToken: async (): Promise<LoginResponse> => {
    const response = await api.post('/auth/refresh');
    return response.data;
  },

  me: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};





// Upload API
export const uploadAPI = {
  uploadProductImage: async (file: File): Promise<{ success: boolean; imageUrl: string; filename: string }> => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      // CSRF token'ı al
      const csrf = await getCSRFToken();
      // Sadece development'ta detaylı bilgileri göster
      if (process.env.NODE_ENV === 'development') {
      }

      const response = await api.post('/upload/product-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRF-Token': csrf,
        },
      });

      return response.data;
    } catch (error: any) {
      throw error;
    }
  },
};

// Orders API
export const ordersAPI = {
  getAll: async (date?: string, status?: string): Promise<Order[]> => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (status) params.append('status', status);

    const response = await api.get(`/orders?${params.toString()}`);
    return response.data;
  },

  create: async (orderData: CreateOrderRequest): Promise<{ id: number }> => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },

  update: async (id: number, orderData: Partial<Order>): Promise<Order> => {
    const response = await api.patch(`/orders/${id}`, orderData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/orders/${id}`);
  },

  complete: async (orderId: number): Promise<void> => {
    await api.patch(`/orders/${orderId}/complete`);
  },

  getKitchenOrders: async (): Promise<Order[]> => {
    const response = await api.get('/orders/kitchen');
    return response.data;
  },

  close: async (id: number): Promise<void> => {
    await api.post(`/orders/${id}/close`);
  },

  pay: async (id: number, payment_type: 'nakit' | 'kart' | 'qr' | 'sumup', gift_code_id?: number, gift_code_discount?: number): Promise<any> => {
    const response = await api.post(`/orders/${id}/pay`, { payment_type, gift_code_id, gift_code_discount });
    return response.data;
  },

  // SumUp ödeme durumu kontrol et
  getSumUpStatus: async (id: number, gift_code_id?: number, gift_code_discount?: number): Promise<any> => {
    const params = new URLSearchParams();
    if (gift_code_id) params.append('gift_code_id', gift_code_id.toString());
    if (gift_code_discount) params.append('gift_code_discount', gift_code_discount.toString());
    const response = await api.get(`/orders/${id}/sumup-status?${params.toString()}`);
    return response.data;
  },

  cancel: async (id: number): Promise<void> => {
    await api.post(`/orders/${id}/cancel`);
  },

  markReady: async (id: number): Promise<void> => {
    await api.patch(`/orders/${id}/ready`);
  },

  getById: async (id: number): Promise<Order> => {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },

  requestReceipt: async (id: number): Promise<void> => {
    await api.patch(`/orders/${id}/request-receipt`);
  },

  getReceiptRequested: async (): Promise<Order[]> => {
    const response = await api.get('/orders/receipt-requested');
    return response.data;
  },

  // Gizli admin için ürün toplamları
  getHiddenAdminProductTotals: async (date?: string, startDate?: string, endDate?: string): Promise<{
    products: Array<{
      product_id: number;
      product_name: string;
      total_quantity: number;
      order_count: number;
    }>;
    totalOrders: number;
  }> => {

    try {
      let url = '/orders/hidden-admin/product-totals?';
      if (startDate && endDate) {
        url += `startDate=${startDate}&endDate=${endDate}`;
      } else if (date) {
        url += `date=${date}`;
      }
      const response = await api.get(url);

      return response.data;
    } catch (error) {
      // console.error('❌ Gizli admin ürün toplamları hatası:', error);
      throw error;
    }
  },

  // Gizli admin için değişiklikleri kaydet
  saveHiddenAdminQuantities: async (productUpdates: Array<{
    product_id: number;
    new_quantity: number;
  }>, dateParams?: { date?: string; startDate?: string; endDate?: string }): Promise<{ message: string; updated_products: number; updated_orders?: number }> => {

    try {
      // Array'i object'e dönüştür - backend quantities object bekliyor
      const quantities: { [key: string]: number } = {};
      productUpdates.forEach(update => {
        // Güvenlik kontrolü ekle
        if (update && update.product_id !== undefined && update.product_id !== null) {
          quantities[update.product_id.toString()] = update.new_quantity;
        }
      });

      const requestBody: any = { quantities };

      // Tarih bilgisini de ekle
      if (dateParams) {
        if (dateParams.date) {
          requestBody.date = dateParams.date;
        } else if (dateParams.startDate && dateParams.endDate) {
          requestBody.startDate = dateParams.startDate;
          requestBody.endDate = dateParams.endDate;
        }
      }

      const response = await api.patch('/orders/hidden-admin/save-quantities', requestBody);

      return response.data;
    } catch (error) {
      console.error('❌ Gizli admin kaydetme hatası:', error);
      throw error;
    }
  },

  // Gizli admin için orijinal ürün toplamları
  getHiddenAdminOriginalTotals: async (date?: string, startDate?: string, endDate?: string): Promise<Array<{
    product_id: number;
    product_name: string;
    original_quantity: number;
    order_count: number;
  }>> => {

    try {
      let url = '/orders/hidden-admin/original-totals?';
      if (startDate && endDate) {
        url += `startDate=${startDate}&endDate=${endDate}`;
      } else if (date) {
        url += `date=${date}`;
      }
      const response = await api.get(url);

      return response.data;
    } catch (error) {
      // console.error('❌ Gizli admin orijinal ürün toplamları hatası:', error);
      throw error;
    }
  },

  // Tüm siparişleri hazır yap
  bulkReady: async (): Promise<{ success: boolean; message: string; updatedOrders: Order[] }> => {
    const response = await api.post('/orders/bulk-ready');
    return response.data;
  },

};

// Tables API
export const tablesAPI = {
  getAll: async () => {
    const response = await api.get('/tables');
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/tables/${id}`);
    return response.data;
  },

  create: async (tableData: any) => {
    const response = await api.post('/tables', tableData);
    return response.data;
  },

  update: async (id: number, tableData: any) => {
    await api.put(`/tables/${id}`, tableData);
  },

  delete: async (id: number) => {
    await api.delete(`/tables/${id}`);
  }
};

// Reports API
export const reportsAPI = {
  getDailySales: async (date: string) => {
    const response = await api.get(`/reports/daily-sales?date=${date}`);
    return response.data;
  },

  getDailySummary: async (date?: string) => {
    const params = date ? `?date=${date}` : '';
    const response = await api.get(`/reports/daily-summary${params}`);
    return response.data;
  },

  getMonthlySummary: async (year: number, month: number) => {
    const response = await api.get(`/reports/monthly-summary?year=${year}&month=${month}`);
    return response.data;
  },

  getSalesReport: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/reports/sales${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  getOriginalSalesReport: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/reports/sales-original${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  getChartData: async (type: 'daily' | 'weekly' | 'monthly', startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/reports/chart-data/${type}${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  getCategoryDistribution: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/reports/category-distribution${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  getMonthlySales: async (month: string) => {
    const response = await api.get(`/reports/monthly-sales?month=${month}`);
    return response.data;
  },

  getTopProducts: async (period: string) => {
    const response = await api.get(`/reports/top-products?period=${period}`);
    return response.data;
  },

  getCodeReports: async (startDate?: string, endDate?: string) => {


    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const url = `/reports/codes${params.toString() ? '?' + params.toString() : ''}`;


    try {
      const response = await api.get(url);

      return response.data;
    } catch (error) {
      // console.error('❌ API hatası:', error);
      throw error;
    }
  }
};

// Taxes API
export const taxesAPI = {
  getTaxReport: async (date: string) => {
    const response = await api.get(`/taxes/report?date=${date}`);
    return response.data;
  },
  getNotes: async () => {
    const response = await api.get('/taxes/notes');
    return response.data;
  },
  saveNote: async (date: string, note: string) => {
    const response = await api.post('/taxes/notes', { date, note });
    return response.data;
  },
  deleteNote: async (date: string) => {
    const response = await api.delete(`/taxes/notes/${date}`);
    return response.data;
  },
  exportMonthlyReport: async (year: number, month: number) => {
    const response = await api.get(`/taxes/export/monthly?year=${year}&month=${month}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

// Settings API
export const settingsAPI = {
  getAll: async () => {
    const response = await api.get('/settings/all');
    return response.data;
  },

  getGlobalSettings: async () => {
    const response = await api.get('/settings');
    return { success: true, data: response.data };
  },

  update: async (key: string, value: string) => {
    const response = await api.put('/settings/single', { key, value });
    return response.data;
  },

  getReceiptSettings: async () => {
    const response = await api.get('/settings/receipt');
    return response.data;
  },

  updateReceiptSettings: async (settings: any) => {
    const response = await api.put('/settings/receipt', settings);
    return response.data;
  },

  verifyHiddenAdminPin: async (pin: string): Promise<{ success: boolean; message: string; isDefault?: boolean }> => {
    const response = await api.post('/settings/hidden-admin/verify-pin', { pin });
    return response.data;
  },

  updateHiddenAdminPin: async (newPin: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.put('/settings/hidden-admin/pin', { newPin });
    return response.data;
  }
};

// Products API
export const productsAPI = {
  getAll: async (): Promise<Product[]> => {
    const response = await api.get('/products');
    return response.data;
  },

  getLowStock: async (): Promise<{ id: number; name: string; stock_quantity: number; category_name: string }[]> => {
    const response = await api.get('/products/low-stock');
    return response.data;
  },

  getCategories: async (): Promise<Category[]> => {
    const response = await api.get('/products/categories');
    return response.data;
  },

  createCategory: async (name: string): Promise<{ id: number }> => {
    const response = await api.post('/products/categories', { name });
    return response.data;
  },

  updateCategory: async (id: number, name: string): Promise<void> => {
    await api.put(`/products/categories/${id}`, { name });
  },

  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`/products/categories/${id}`);
  },

  create: async (productData: any): Promise<{ id: number; message: string }> => {
    const response = await api.post('/products', productData);
    return response.data;
  },

  update: async (id: number, productData: any): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/products/${id}`, productData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/products/${id}`);
  },

  reduceStock: async (id: number, quantity: number): Promise<void> => {
    await api.patch(`/products/${id}/stock`, { quantity });
  }
};

// Categories API
export const categoriesAPI = {
  getAll: async () => {
    const response = await api.get('/products/categories');
    return response.data;
  },

  create: async (categoryData: { name: string }) => {
    const response = await api.post('/products/categories', categoryData);
    return response.data;
  },

  update: async (id: number, categoryData: { name: string }) => {
    const response = await api.put(`/products/categories/${id}`, categoryData);
    return response.data;
  },

  delete: async (id: number) => {
    await api.delete(`/products/categories/${id}`);
  }
};

// Users API
export const usersAPI = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get('/users');
    return response.data;
  },

  create: async (userData: {
    username: string;
    password: string;
    role: 'admin' | 'garson' | 'mutfak';
    can_process_payments?: boolean;
  }): Promise<{ id: number; message: string }> => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  update: async (id: number, userData: Partial<User>): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  changePassword: async (id: number, newPassword: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/users/${id}/password`, { password: newPassword });
    return response.data;
  }
};



// Updates API
export const updatesAPI = {
  check: async () => {
    const response = await api.get('/updates/check');
    return response.data;
  },

  checkForUpdates: async () => {
    const response = await api.get('/updates/check');
    return response.data;
  },

  getUpdateHistory: async () => {
    const response = await api.get('/updates/history', { timeout: 10000 }); // 10 saniye timeout
    return response.data;
  },

  getSystemInfo: async () => {
    const response = await api.get('/updates/system-info');
    return response.data;
  },

  getSystemStatus: async () => {
    const response = await api.get('/updates/status');
    return response.data;
  },

  downloadUpdate: async (updateId: number) => {
    const response = await api.get(`/updates/download/${updateId}`);
    return response.data;
  },

  startAutoUpdateCheck: async () => {
    const response = await api.post('/updates/auto-check');
    return response.data;
  }
};

// Gift Codes API
export const giftCodesAPI = {
  // Tüm hediye kodlarını getir
  getAll: async () => {
    const response = await api.get('/gift-codes');
    return response.data;
  },

  // Hediye kodu oluştur
  create: async (giftCodeData: any) => {
    const response = await api.post('/gift-codes', giftCodeData);
    return response.data;
  },

  // Hediye kodu güncelle
  update: async (id: number, giftCodeData: any) => {
    const response = await api.put(`/gift-codes/${id}`, giftCodeData);
    return response.data;
  },

  // Hediye kodu sil
  delete: async (id: number) => {
    const response = await api.delete(`/gift-codes/${id}`);
    return response.data;
  },

  // Hediye kodu doğrula (sipariş sırasında)
  validate: async (code: string, cart_total: number, cart_items: any[]) => {
    const response = await api.post('/gift-codes/validate', {
      code,
      cart_total,
      cart_items
    });
    return response.data;
  },

  // Hediye kodu kullan
  use: async (gift_code_id: number, order_id: number, customer_info: string, discount_amount: number) => {
    const response = await api.post('/gift-codes/use', {
      gift_code_id,
      order_id,
      customer_info,
      discount_amount
    });
    return response.data;
  },

  // Kullanım geçmişi
  getUsageHistory: async () => {
    const response = await api.get('/gift-codes/usage-history');
    return response.data;
  },

  // Sadakat kuralları
  getLoyaltyRules: async () => {
    const response = await api.get('/gift-codes/loyalty-rules');
    return response.data;
  },

  // Sadakat kuralı oluştur
  createLoyaltyRule: async (ruleData: any) => {
    const response = await api.post('/gift-codes/loyalty-rules', ruleData);
    return response.data;
  },

  // Sadakat kuralı sil
  deleteLoyaltyRule: async (id: number) => {
    const response = await api.delete(`/gift-codes/loyalty-rules/${id}`);
    return response.data;
  }
};

// Customers API
export const customersAPI = {
  getAll: async (): Promise<any[]> => {
    const response = await api.get('/customers');
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/customers/${id}`);
    return response.data;
  },

  create: async (customerData: { name: string }): Promise<any> => {
    const response = await api.post('/customers', customerData);
    return response.data;
  },

  addPurchase: async (customerId: number, purchaseData: { product_id: number; quantity: number; order_id: number }): Promise<any> => {
    const response = await api.post(`/customers/${customerId}/purchases`, purchaseData);
    return response.data;
  },

  getLoyaltyStatus: async (customerId: number): Promise<any[]> => {
    const response = await api.get(`/customers/${customerId}/loyalty`);
    return response.data;
  }
};


// Loyalty Codes API
export const loyaltyCodesAPI = {
  getAll: async (page = 1, limit = 25, status = 'all'): Promise<any> => {
    try {
      const response = await api.get(`/loyalty-codes?page=${page}&limit=${limit}&status=${status}`);
      return response.data;
    } catch (error: any) {
      // console.error('❌ Sadakat kodları getirme hatası:', error);
      throw new Error(error.response?.data?.error || 'Sadakat kodları getirilemedi');
    }
  },

  create: async (data: { rule_id: number; quantity?: number }): Promise<any> => {
    const response = await api.post('/loyalty-codes', data);
    return response.data;
  },

  use: async (codeId: number, quantity?: number): Promise<any> => {
    const response = await api.put(`/loyalty-codes/${codeId}/use`, { quantity });
    return response.data;
  },

  revert: async (codeId: number, quantity?: number): Promise<any> => {
    const response = await api.put(`/loyalty-codes/${codeId}/revert`, { quantity });
    return response.data;
  },

  useReward: async (codeId: number, productId: number, tempLoyaltyCount?: number): Promise<any> => {
    const response = await api.put(`/loyalty-codes/${codeId}/use-reward`, { productId, tempLoyaltyCount });
    return response.data;
  },

  getCompleted: async (): Promise<any[]> => {
    const response = await api.get('/loyalty-codes/completed');
    return response.data;
  },

  delete: async (codeId: number): Promise<void> => {
    await api.delete(`/loyalty-codes/${codeId}`);
  },

  bulkDelete: async (ids: number[]): Promise<any> => {
    const response = await api.post('/loyalty-codes/bulk-delete', { ids });
    return response.data;
  },

  bulkDeleteCompleted: async (ids: number[]): Promise<any> => {
    const response = await api.post('/loyalty-codes/bulk-delete-completed', { ids });
    return response.data;
  },

  validate: async (code: string): Promise<any> => {
    const response = await api.post('/loyalty-codes/validate', { code });
    return response.data;
  }
};

// Realtime Cart API
export const realtimeCartAPI = {
  update: async (data: any): Promise<any> => {
    const response = await api.post('/realtime-cart/update', data);
    return response.data;
  },

  getAdminState: async (): Promise<any> => {
    const response = await api.get('/realtime-cart/admin-state');
    return response.data;
  },

  sendQRPayment: async (qrData: { orderId: number; qrCodeUrl: string; amount: number; description: string; expiresAt?: string; paymentId?: string; deepLinkUrl?: string }): Promise<any> => {
    const response = await api.post('/realtime-cart/qr-payment', qrData);
    return response.data;
  },

  clearQRPayment: async (): Promise<any> => {
    const response = await api.post('/realtime-cart/clear-qr-payment');
    return response.data;
  },

  clearAfterPayment: async (): Promise<any> => {
    const response = await api.post('/realtime-cart/clear-after-payment');
    return response.data;
  },

  forceClearQR: async (): Promise<any> => {
    const response = await api.post('/realtime-cart/force-clear-qr');
    return response.data;
  }
};

// Payconiq QR Payment API - Online (V3) Custom implementation
export const payconiqAPI = {
  // QR ödeme başlat
  initiatePayment: async (orderId: number, amount: number, description?: string): Promise<any> => {
    // OrderId validation
    if (!orderId || isNaN(orderId) || orderId <= 0) {
      throw new Error('Geçerli bir sipariş ID gerekli');
    }

    try {
      const response = await api.post(`/orders/${orderId}/initiate-qr-payment`, {
        amount,
        description
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  // QR ödeme durumu kontrol et
  getPaymentStatus: async (orderId: number): Promise<any> => {
    const response = await api.get(`/orders/${orderId}/payment-status`);
    return response.data;
  },

  // QR ödeme iptal et
  cancelPayment: async (orderId: number): Promise<any> => {
    const response = await api.post(`/orders/${orderId}/cancel-qr-payment`);
    return response.data;
  },

  // Payconiq servis test et
  testService: async (): Promise<any> => {
    const response = await api.get('/orders/test-payconiq');
    return response.data;
  }
};

export default api;
